<script>
import { computed, reactive, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import {
  required,
  email,
  maxLength,
  alphaNum,
  helpers,
} from '@vuelidate/validators'
import axios from 'axios'
import InputErrors from './components/InputErrors.vue'
import Notification from './components/Notification.vue'

export default {
  props: ['params'],
  components: {
    InputErrors,
    Notification,
  },
  setup() {
    const loading = ref(false)
    const notifications = ref([])

    const requiredGerman = helpers.withMessage(
      'Dies ist ein Pflichtfeld.',
      required
    )
    const emailGerman = helpers.withMessage(
      'Bitte geben Sie eine gültige E-Mail-Adresse an.',
      email
    )
    const maxLengthFramenumberGerman = helpers.withMessage(
      'Die Eingabe darf nicht länger als 20 Zeichen sein.',
      maxLength(20)
    )
    const alphaNumGerman = helpers.withMessage(
      'Die Eingabe muss alpha-nummerisch sein.',
      alphaNum
    )

    const state = reactive({
      firstname: '',
      lastname: '',
      postcode: '',
      city: '',
      email: '',
      phone: '',
      framenumber: '',
      purchaseDate: '',
      retailer: '',
      receipt: null,
    })

    const fileReceipt = ref(null)
    const dropzone = ref({
      fileOverDropzone: false,
    })

    const rules = computed(() => {
      return {
        firstname: { requiredGerman },
        lastname: { requiredGerman },
        postcode: { requiredGerman },
        city: { requiredGerman },
        email: { requiredGerman, emailGerman },
        framenumber: {
          requiredGerman,
          maxLengthFramenumberGerman,
          alphaNumGerman,
        },
        purchaseDate: { requiredGerman },
        retailer: { requiredGerman },
        receipt: { requiredGerman },
      }
    })

    const v$ = useVuelidate(rules, state)

    return { loading, notifications, state, v$, fileReceipt, dropzone }
  },
  methods: {
    async onFormSubmit() {
      this.notifications = []

      const isFormValid = await this.v$.$validate()
      if (!isFormValid) {
        return
      }

      // reset validation "watch mode"
      this.$nextTick(() => {
        this.v$.$reset()
      })

      const baseUrl =
        process.env.NODE_ENV === 'production'
          ? 'https://ewms.hnf-nicolai.com'
          : 'http://localhost:5000'

      this.loading = true

      const formData = new FormData()

      for (const key in this.state) {
        if (Object.hasOwnProperty.call(this.state, key)) {
          formData.append(key, this.state[key])
        }
      }

      try {
        const res = await axios.post(
          `${baseUrl}/bike-registration/register`,
          formData
        )

        if (res.status === 201) {
          // this.notifications.push({
          //   type: 'success',
          //   message:
          //     'Ihre Daten wurden erfolgreich übermittelt. Bitte schließen Sie den Vorgang ab, indem Sie die E-Mail in Ihrem Postfach bestätigen.',
          // })

          this.resetState()

          // redirect to DOI Info Page
          window.location.href = 'https://www.hnf-nicolai.com/checkyourmail/'
        } else {
          this.notifications.push({
            type: 'warning',
            message:
              'Leider kam es zu einem Fehler in der Übertragung. Bitte versuchen Sie es erneut.',
          })
        }
      } catch (err) {
        console.log(err)
        this.notifications.push({
          type: 'error',
          message:
            'Leider kam es zu einem Fehler in der Übertragung. Bitte versuchen Sie es erneut.',
        })
      } finally {
        this.loading = false
      }
    },
    onFileChange(ev) {
      if (this.loading) {
        return
      }

      const file =
        ev.target.files && ev.target.files.length ? ev.target.files[0] : null

      this.state.receipt = file
    },
    onRemoveAttachment() {
      this.state.receipt = null
    },
    removeNotification(index) {
      this.notifications.splice(index, 1)
    },
    resetState() {
      for (const key in this.state) {
        if (key === 'receipt') {
          this.state[key] = null
          continue
        }
        this.state[key] = ''
      }
    },
    dropFile(e) {
      let droppedFiles = e.dataTransfer.files
      if (!droppedFiles) return

      this.state.receipt = droppedFiles[0]
      this.dropzone.fileOverDropzone = false
    },
  },
}
</script>

<template>
  <div class="container mx-auto">
    <h3 v-if="params.title">{{ params.title }}</h3>
    <form
      ref="formElement"
      autocomplete="off"
      role="form"
      enctype="multipart/form-data"
      @submit.prevent="onFormSubmit"
    >
      <div v-if="notifications && notifications.length" class="space-y-2 mb-4">
        <Notification
          v-for="(item, index) in notifications"
          :key="index"
          :type="item.type"
          @dismiss="removeNotification(index)"
          >{{ item.message }}</Notification
        >
      </div>
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <div class="lg:col-span-2">
          <label for="firstname" class="block text-sm font-medium text-gray-700"
            >Vorname</label
          >
          <div class="mt-1">
            <input
              id="firstname"
              type="text"
              v-model="state.firstname"
              class="shadow-sm block w-full sm:text-sm rounded-md focus:outline-none"
              :class="
                v$.firstname.$error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
              "
              :disabled="loading"
            />
          </div>
          <InputErrors :errors="v$.firstname.$errors"></InputErrors>
        </div>
        <div class="lg:col-span-2">
          <label for="name" class="block text-sm font-medium text-gray-700"
            >Nachname</label
          >
          <div class="mt-1">
            <input
              id="name"
              type="text"
              v-model="state.lastname"
              class="shadow-sm block w-full sm:text-sm rounded-md focus:outline-none"
              :class="
                v$.lastname.$error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
              "
              :disabled="loading"
            />
          </div>
          <InputErrors :errors="v$.lastname.$errors"></InputErrors>
        </div>
        <div class="lg:col-span-2">
          <label for="postcode" class="block text-sm font-medium text-gray-700"
            >Postleitzahl</label
          >
          <div class="mt-1">
            <input
              id="postcode"
              type="text"
              v-model="state.postcode"
              class="shadow-sm block w-full sm:text-sm rounded-md focus:outline-none"
              :class="
                v$.postcode.$error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
              "
              :disabled="loading"
            />
          </div>
          <InputErrors :errors="v$.postcode.$errors"></InputErrors>
        </div>
        <div class="lg:col-span-2">
          <label for="city" class="block text-sm font-medium text-gray-700"
            >Ort</label
          >
          <div class="mt-1">
            <input
              id="city"
              type="text"
              v-model="state.city"
              class="shadow-sm block w-full sm:text-sm rounded-md focus:outline-none"
              :class="
                v$.city.$error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
              "
              :disabled="loading"
            />
          </div>
          <InputErrors :errors="v$.city.$errors"></InputErrors>
        </div>
        <div class="lg:col-span-2">
          <label for="email" class="block text-sm font-medium text-gray-700"
            >Email</label
          >
          <div class="mt-1">
            <input
              id="email"
              type="text"
              v-model="state.email"
              class="shadow-sm block w-full sm:text-sm rounded-md focus:outline-none"
              :class="
                v$.email.$error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
              "
              :disabled="loading"
            />
          </div>
          <InputErrors :errors="v$.email.$errors"></InputErrors>
        </div>
        <div class="lg:col-span-2">
          <label for="phone" class="block text-sm font-medium text-gray-700"
            >Telefon</label
          >
          <div class="mt-1">
            <input
              id="phone"
              type="text"
              v-model="state.phone"
              class="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md focus:outline-none focus:ring-gray-500 focus:border-gray-500"
              :disabled="loading"
            />
          </div>
        </div>
      </div>
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <div class="lg:col-span-2">
          <label
            for="framenumber"
            class="block text-sm font-medium text-gray-700"
            >Rahmennummer</label
          >
          <div class="mt-1">
            <input
              id="framenumber"
              type="text"
              v-model="state.framenumber"
              class="shadow-sm block w-full sm:text-sm rounded-md focus:outline-none"
              :class="
                v$.framenumber.$error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
              "
              :disabled="loading"
            />
          </div>
          <InputErrors :errors="v$.framenumber.$errors"></InputErrors>
        </div>
        <div class="lg:col-span-2"></div>
        <div class="lg:col-span-1">
          <label
            for="purchasedate"
            class="block text-sm font-medium text-gray-700"
            >Kaufdatum</label
          >
          <div class="mt-1">
            <input
              id="purchasedate"
              type="date"
              :max="new Date().toISOString().split('T')[0]"
              v-model="state.purchaseDate"
              class="shadow-sm block w-full sm:text-sm rounded-md focus:outline-none"
              :class="[
                v$.purchaseDate.$error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500',
                state.purchaseDate && state.purchaseDate.length
                  ? ''
                  : 'text-gray-400',
              ]"
              :disabled="loading"
            />
          </div>
          <InputErrors :errors="v$.purchaseDate.$errors"></InputErrors>
        </div>
        <div class="lg:col-span-3">
          <label for="retailer" class="block text-sm font-medium text-gray-700"
            >Händler</label
          >
          <div class="mt-1">
            <input
              id="retailer"
              type="text"
              v-model="state.retailer"
              class="shadow-sm block w-full sm:text-sm rounded-md focus:outline-none"
              :class="
                v$.retailer.$error
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
              "
              :disabled="loading"
            />
          </div>
          <InputErrors :errors="v$.retailer.$errors"></InputErrors>
        </div>
        <div class="md:col-span-2 lg:col-span-4">
          <label class="block text-sm font-medium text-gray-700">
            Kaufbeleg
          </label>
          <div v-if="state.receipt" class="mt-2">
            <button
              type="button"
              class="flex items-center cursor-pointer"
              @click="onRemoveAttachment"
            >
              <span>{{ state.receipt.name }}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4 ml-2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div
            v-else
            class="group mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md cursor-pointer"
            :class="[
              v$.receipt.$error
                ? 'border-red-300'
                : dropzone.fileOverDropzone
                ? 'border-gray-600'
                : 'border-gray-300',
            ]"
            @dragover.prevent
            @dragenter.prevent="dropzone.fileOverDropzone = true"
            @dragleave.prevent="dropzone.fileOverDropzone = false"
            @drop.prevent="dropFile"
            @click="fileReceipt.click()"
          >
            <div class="space-y-1 text-center">
              <div class="space-y-1 text-center">
                <svg
                  class="mx-auto h-12 w-12 text-gray-500 group-hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  ></path>
                </svg>
                <div class="flex justify-center text-sm">
                  <label
                    for="receipt"
                    class="relative cursor-pointer bg-white rounded-md font-medium text-gray-500 group-hover:text-gray-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-gray-500"
                  >
                    <span>Kaufbeleg anhängen oder drag and drop</span>
                    <input
                      id="receipt"
                      ref="fileReceipt"
                      type="file"
                      @change="onFileChange"
                      class="sr-only"
                      accept="image/png, image/jpeg, .pdf"
                    />
                  </label>
                </div>
              </div>
              <p class="text-xs !text-gray-500">PNG, JPG, PDF bis zu 10MB</p>
            </div>
          </div>
          <InputErrors :errors="v$.receipt.$errors"></InputErrors>
        </div>
      </div>
      <div class="mt-4">
        <button
          type="button"
          class="inline-flex items-center justify-center px-6 py-3 w-full md:w-1/2 lg:w-1/3 border border-transparent text-xs uppercase leading-6 font-medium rounded-md text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:border-gray-900 focus:shadow-outline-gray active:bg-gray-900 transition ease-in-out duration-150"
          :disabled="loading"
          @click="onFormSubmit"
        >
          <span v-if="!loading">Registrieren</span>
          <span v-else>Registrieren</span>
        </button>
      </div>
    </form>
  </div>
</template>
